import { Link } from 'react-router-dom';
import Icon from '../components/Icons';
import { Logo } from '../components/Layout';
import Barcode from '../components/Barcode';
import { company, fmtDate } from '../data/common';
import './documents.css';

const statusBadge = {
  confirmed: ['Confirmed', 'ok'],
  pending: ['Awaiting confirmation', 'wait'],
  held: ['On hold — not ticketed', 'wait'],
  cancelled: ['Cancelled', 'void'],
};

/** A labelled value in the document's reference strip. */
export function DocField({ label, children, strong }) {
  return (
    <div className={`doc-field ${strong ? 'strong' : ''}`}>
      <span>{label}</span>
      <b>{children || '—'}</b>
    </div>
  );
}

export function DocSection({ title, children, className = '' }) {
  return (
    <section className={`doc-section ${className}`}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/**
 * A4 printable document (e-ticket, voucher) with the company letterhead, references, a status
 * banner for documents that aren't valid yet, and a print toolbar hidden on paper.
 */
export default function DocumentShell({ title, subtitle, booking, references, barcode, notice, children, backTo }) {
  const [statusLabel, statusClass] = statusBadge[booking.status] || statusBadge.confirmed;
  const issued = fmtDate(booking.createdAt.slice(0, 10), { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <div className="doc-page">
      <div className="doc-toolbar">
        <Link to={backTo} className="btn btn-ghost btn-sm">← Back to booking</Link>
        <span className="muted small">Choose “Save as PDF” in the print dialog to download.</span>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}><Icon name="file" size={14} /> Print / Save as PDF</button>
      </div>

      <article className={`doc ${booking.status === 'cancelled' ? 'doc-void' : ''}`} aria-label={title}>
        <header className="doc-head">
          <div className="doc-brand">
            <Logo />
            <span>{company.name}</span>
          </div>
          <div className="doc-title">
            <h1>{title}</h1>
            {subtitle && <span>{subtitle}</span>}
            <span className={`doc-status ${statusClass}`}>{statusLabel}</span>
          </div>
        </header>

        <div className="doc-refs">
          <div className="doc-ref-fields">
            {references.map(([label, value, strong]) => <DocField key={label} label={label} strong={strong}>{value}</DocField>)}
            <DocField label="Issued on">{issued}</DocField>
          </div>
          {barcode && <div className="doc-barcode"><Barcode value={barcode} height={44} label={`Booking barcode ${barcode}`} /></div>}
        </div>

        {notice && <div className={`doc-notice ${statusClass}`} role="note">{notice}</div>}

        {children}

        <footer className="doc-foot">
          <div>
            <b>Need help?</b> Call {company.brand} 24x7 support on <b>{company.phone}</b> and quote booking ID <b>{booking.id}</b>.
            You can also manage this booking from My Trips.
          </div>
          <div className="muted">
            Issued by {company.name}. This is a computer-generated document and needs no signature.
            Amounts are in Indian Rupees (INR).
          </div>
        </footer>
      </article>
    </div>
  );
}
